import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ChevronDown, Users, Target, Briefcase, Award,
  ClipboardCheck, Code, Brain, Layout, BarChart, Shield, MapPin, Quote,
  ChevronLeft, ChevronRight, Sparkles,
} from 'lucide-react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import PublicLayout from '../components/public/PublicLayout';
import DarkSection from '../components/public/DarkSection';
import Blaze from '../components/canvasui/Blaze';
import {
  EASE, Reveal, Stagger, StaggerItem, KineticText, RotatingWord, CountUp,
  Marquee, Eyebrow, SectionHeading,
} from '../components/public/motion';

// Legacy one-page anchors → new dedicated pages
const HASH_REDIRECTS = {
  about: '/about',
  mission: '/about',
  social: '/about',
  services: '/services',
  specialisations: '/services',
  testimonials: '/testimonials',
  contact: '/contact',
  'job-board': '/jobs',
  login: '/login',
};

const SERVICES_PREVIEW = [
  {
    icon: Users,
    title: 'ICT Workforce Augmentation',
    desc: 'Pre-vetted professionals embedded in your delivery teams for contract or surge needs.',
    big: true,
  },
  {
    icon: Briefcase,
    title: 'Government Labour Hire',
    desc: 'End-to-end ICT recruitment for Commonwealth and State agencies, aligned with SME procurement goals.',
    big: true,
  },
  {
    icon: Target,
    title: 'Buyer-Sourced Procurement',
    desc: 'Fair-rate labour hire for panels and buyer-sourced models, with transparent oncosts.',
  },
  {
    icon: Award,
    title: 'Permanent ICT Recruitment',
    desc: 'From junior to principal-level hires for enduring public and private sector roles.',
  },
  {
    icon: Brain,
    title: 'AI-Powered Job Matching',
    desc: 'In-house tools match roles with high-fit candidates faster and more accurately.',
  },
  {
    icon: ClipboardCheck,
    title: 'Resume Optimisation',
    desc: 'ATS-ready resume and LinkedIn support for government ICT roles.',
  },
  {
    icon: Code,
    title: 'Technical Skills Testing',
    desc: 'Tech screening, coding tests and behavioural interviews to validate capability.',
  },
  {
    icon: Layout,
    title: 'Web Design & Development',
    desc: 'UI/UX design and front-end development for portals and citizen-facing platforms.',
  },
  {
    icon: BarChart,
    title: 'Workforce Planning Consultancy',
    desc: 'Strategic workforce advice and ICT skill-gap analysis.',
  },
];

const SPECIALISATIONS_ROW_A = [
  'Data & AI', 'Cloud Engineering & DevOps', 'Software Engineering', 'Cybersecurity & Risk',
  'Program & Project Delivery', 'Enterprise Architecture', 'Integration & Middleware',
];

const SPECIALISATIONS_ROW_B = [
  'CRM & ERP Platforms', 'Business Analysis & UX', 'QA & Testing', 'Network & Infrastructure',
  'Agile & Coaching', 'Service Desk', 'Training & Knowledge',
];

const TESTIMONIALS = [
  {
    text: 'I really appreciate the support and guidance provided by the Appsytec team. Their advice is practical, encouraging, and insightful, which can be very helpful for candidates navigating their career journey. It’s great to see an organisation that genuinely focuses on helping people succeed and connect with the right opportunities.',
    author: 'Navanish S.',
  },
  {
    text: "I've been very impressed thus far with the attention to detail and level of service provided by Appsytec. Readily on hand to answer specific questions and help me understand the government contract landscape in my first contract role. Really appreciate all the support and would very much recommend.",
    author: 'Timothy D.',
  },
  {
    text: 'Appsytec was great throughout the process and provided good feedback when required. They kept me up to date on the progress of my application. I would recommend Appsytec to others.',
    author: 'Margery T.',
  },
];

const CANDIDATE_TEASERS = [
  { role: 'Senior Data Engineer',       type: 'Data & AI',              seniority: 'Senior',     clearance: 'NV1',      location: 'Canberra, ACT'  },
  { role: 'Cloud / DevOps Lead',        type: 'Cloud & DevOps',         seniority: 'Lead',       clearance: 'NV2',      location: 'Melbourne, VIC' },
  { role: 'Full-Stack Developer',       type: 'Software Engineering',   seniority: 'Mid career', clearance: 'Baseline', location: 'Sydney, NSW'    },
  { role: 'Cyber Security Analyst',     type: 'Cybersecurity',          seniority: 'Mid career', clearance: 'NV1',      location: 'Canberra, ACT'  },
  { role: 'Solution Architect',         type: 'Architecture',           seniority: 'Senior',     clearance: 'TSPV',     location: 'Canberra, ACT'  },
  { role: 'Program Manager',            type: 'Project Delivery',       seniority: 'Management', clearance: 'NV2',      location: 'Canberra, ACT'  },
  { role: 'Machine Learning Engineer',  type: 'Data & AI',              seniority: 'Senior',     clearance: 'None',     location: 'Remote'         },
];

const CLEARANCE_DARK = {
  TSPV:     'bg-purple-500/20 text-purple-300 border-purple-400/30',
  NV2:      'bg-blue-500/20   text-blue-300   border-blue-400/30',
  NV1:      'bg-cyan-500/20   text-cyan-300   border-cyan-400/30',
  Baseline: 'bg-slate-500/20  text-slate-300  border-slate-400/30',
  None:     'bg-slate-500/10  text-slate-500  border-slate-500/20',
};

const SENIORITY_DARK = {
  Junior:       'bg-green-500/15  text-green-400  border-green-400/30',
  'Mid career': 'bg-teal-500/15   text-teal-400   border-teal-400/30',
  Senior:       'bg-blue-500/15   text-blue-400   border-blue-400/30',
  Lead:         'bg-indigo-500/15 text-indigo-400 border-indigo-400/30',
  Management:   'bg-amber-500/15  text-amber-400  border-amber-400/30',
  Executive:    'bg-rose-500/15   text-rose-400   border-rose-400/30',
};

/* --------------------------------- Hero ------------------------------------ */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <Blaze
      height={0.23}
      distortion={0.6}
      distortionScale={0.5}
      speed={1}
      sparks={0.5}
      sparkDensity={1.5}
      sparkSize={1}
      layers={4}
      smoke={1.4}
      glow={1.5}
      sparkColor={[1, 1, 1]}
      smokeColor={[1, 0.3412, 0.3412]}
      style={{ minHeight: '100vh' }}
    >
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden bg-gray-900">
      {/* Parallax background image */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src="https://images.unsplash.com/photo-1659991191528-7369a24cf8c0?q=80&w=1974&auto=format&fit=crop"
          alt=""
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-gray-900/60" />
      </motion.div>

      {/* Ambient grid + glow */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-red-500/20 blur-3xl" />
      </div>

      {/* Decorative bracket echoing the logo */}
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 1, ease: EASE }}
        className="pointer-events-none absolute right-8 top-28 hidden h-40 w-40 border-r-4 border-t-4 border-red-500/40 lg:block"
      />
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.35, duration: 1, ease: EASE }}
        className="pointer-events-none absolute bottom-28 left-8 hidden h-40 w-40 border-b-4 border-l-4 border-red-500/40 lg:block"
      />

      <motion.div
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-36 md:px-8"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <Reveal y={16} delay={0.3}>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-300 backdrop-blur-sm md:text-sm">
            <Sparkles className="h-4 w-4 text-red-400" />
            Australian ICT Labour Hire &amp; Recruitment
          </span>
        </Reveal>

        <h1 className="mt-8 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-white md:text-8xl">
          <KineticText text="Connecting" delay={0.45} />
          <br />
          <span className="text-red-500">
            <RotatingWord
              words={['Talent', 'Engineers', 'Architects', 'Analysts', 'Specialists']}
              interval={2800}
            />
          </span>
          <br />
          <KineticText text="with opportunity." delay={0.7} />
        </h1>

        <Reveal delay={1} y={20}>
          <p className="mt-8 max-w-xl text-lg text-gray-300 md:text-xl">
            Your trusted partner for streamlined labour hire, recruitment and ICT consultancy across
            Commonwealth, State and private sectors.
          </p>
        </Reveal>

        <Reveal delay={1.2} y={20}>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/services"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/25"
            >
              Our Services
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/candidates"
              className="group inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white hover:bg-white hover:text-gray-900"
            >
              Available Candidates
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        style={{ opacity: contentOpacity }}
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>
          <ChevronDown className="h-7 w-7 text-white/60" />
        </motion.div>
      </motion.div>
    </section>
    </Blaze>
  );
};

/* ------------------------------- Stats strip -------------------------------- */
const StatsStrip = () => (
  <section className="border-b border-gray-200 bg-white">
    <Stagger className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-14 md:grid-cols-4 md:px-8" gap={0.1}>
      {[
        { value: <CountUp to={100} suffix="%" />, label: 'Australian owned & operated' },
        { value: <CountUp to={9} suffix="+" />, label: 'Core service offerings' },
        { value: <CountUp to={14} />, label: 'ICT specialisation areas' },
        { value: 'TSPV', label: 'Up to top-level security clearances' },
      ].map((stat, i) => (
        <StaggerItem key={i} className="text-center">
          <div className="font-display text-4xl font-extrabold tracking-tight text-gray-900 md:text-5xl">
            {stat.value}
          </div>
          <p className="mt-2 text-sm text-gray-500 md:text-base">{stat.label}</p>
        </StaggerItem>
      ))}
    </Stagger>
  </section>
);

/* ------------------------------ About preview ------------------------------- */
const AboutPreview = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section ref={ref} className="overflow-hidden bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 md:px-8 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal y={16}>
            <Eyebrow>Who we are</Eyebrow>
          </Reveal>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-gray-900 md:text-5xl">
            <KineticText text="Small. Agile. Accountable." />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-gray-600 md:text-xl">
              Appsytec is a boutique Australian SME focused on making procurement effortless. We specialise in
              connecting public sector organisations with high-calibre ICT professionals.
            </p>
            <p className="mt-4 text-gray-600">
              We're not a volume recruiter. We focus on building long-term relationships, delivering transparent
              pricing, and supplying candidates who deliver measurable outcomes from day one.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/about"
              className="group mt-8 inline-flex items-center gap-2 font-semibold text-red-500 transition-colors hover:text-red-600"
            >
              More about us
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="relative">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            <motion.img
              src="https://images.unsplash.com/photo-1594732832278-abd644401426?q=80&w=2070&auto=format&fit=crop&w=1000&q=80"
              alt="Our team collaborating"
              className="h-[26rem] w-full scale-110 object-cover md:h-[30rem]"
              style={{ y: imgY }}
            />
          </div>
          {/* Bracket frame accent */}
          <div aria-hidden="true" className="absolute -left-4 -top-4 h-24 w-24 rounded-tl-3xl border-l-4 border-t-4 border-red-500" />
          <div aria-hidden="true" className="absolute -bottom-4 -right-4 h-24 w-24 rounded-br-3xl border-b-4 border-r-4 border-red-500" />
        </Reveal>
      </div>
    </section>
  );
};

/* ----------------------------- Services bento ------------------------------- */
const ServicesBento = () => (
  <section className="bg-gray-50 py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <SectionHeading
        eyebrow="Our services"
        title="Everything you need to build capability"
        subtitle="Comprehensive workforce solutions tailored to government and enterprise, from surge augmentation to permanent hires."
      />

      <Stagger className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4" gap={0.06}>
        {SERVICES_PREVIEW.map((service, i) => {
          const Icon = service.icon;
          return (
            <StaggerItem key={i} className={service.big ? 'lg:col-span-2' : ''}>
              <Link to="/services" className="block h-full">
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  className="group relative h-full overflow-hidden rounded-3xl border border-gray-200 bg-white p-7 transition-shadow duration-300 hover:shadow-xl hover:shadow-gray-900/5"
                >
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-red-50 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="inline-flex rounded-2xl bg-red-50 p-3 text-red-500 transition-colors duration-300 group-hover:bg-red-500 group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-bold text-gray-900 md:text-xl">{service.title}</h3>
                    <p className="mt-2 text-sm text-gray-600 md:text-base">{service.desc}</p>
                  </div>
                  <ArrowUpRight className="absolute right-6 top-6 h-5 w-5 text-gray-300 transition-all duration-300 group-hover:text-red-500" />
                </motion.div>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal delay={0.2} className="mt-10 text-center">
        <Link
          to="/services"
          className="group inline-flex items-center gap-2 rounded-full bg-gray-900 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-red-500"
        >
          Explore all services
          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

/* -------------------------- Specialisations marquee ------------------------- */
const SpecialisationsMarquee = () => (
  <section className="overflow-hidden border-y border-gray-200 bg-white py-16 md:py-20">
    <SectionHeading
      eyebrow="Specialisations"
      title="Deep expertise, end to end"
      className="mx-auto max-w-7xl px-4 md:px-8"
    />
    <div className="mt-12 space-y-4 py-1.5 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
      <Marquee speed={50}>
        {SPECIALISATIONS_ROW_A.map((item) => (
          <Link
            key={item}
            to="/services"
            className="mx-2 inline-flex whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-6 py-3 font-display text-base font-semibold text-gray-700 transition-colors duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600 md:text-lg"
          >
            {item}
          </Link>
        ))}
      </Marquee>
      <Marquee speed={55} reverse>
        {SPECIALISATIONS_ROW_B.map((item) => (
          <Link
            key={item}
            to="/services"
            className="mx-2 inline-flex whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-6 py-3 font-display text-base font-semibold text-gray-700 transition-colors duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600 md:text-lg"
          >
            {item}
          </Link>
        ))}
      </Marquee>
    </div>
  </section>
);

/* ----------------------------- Candidates teaser ---------------------------- */
const CandidatesTeaser = () => (
  <DarkSection glow="top-right" className="py-20 md:py-28">
    {/* Subtle grid backdrop */}
    <div className="pointer-events-none absolute inset-0">
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
    </div>

    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 md:px-8 lg:grid-cols-2">
      <div>
        <Reveal y={16}>
          <Eyebrow dark>Talent on hand</Eyebrow>
        </Reveal>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight md:text-5xl">
          <KineticText text="Cleared professionals, ready to deploy" />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-6 text-lg text-gray-400">
            Browse a snapshot of vetted ICT specialists currently available for engagement: filter by
            role type, clearance and seniority.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link
            to="/candidates"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-red-500 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/25"
          >
            View available candidates
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      {/* Vertical auto-scrolling ticker — click anything to go to /candidates */}
      <Reveal y={20} delay={0.1}>
        <Link
          to="/candidates"
          className="group block cursor-pointer"
          aria-label="View all available candidates"
        >
          {/* Fade mask top + bottom for seamless look */}
          <div
            className="relative h-[310px] overflow-hidden rounded-2xl"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%)',
            }}
          >
            <div className="animate-marquee-vertical space-y-3 group-hover:[animation-play-state:paused]">
              {/* List duplicated for seamless loop */}
              {[...CANDIDATE_TEASERS, ...CANDIDATE_TEASERS].map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-colors duration-200 group-hover:border-red-500/30 group-hover:bg-white/8"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-widest text-red-400">{c.type}</p>
                    <h3 className="mt-1 truncate font-display text-lg font-bold text-white">{c.role}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${CLEARANCE_DARK[c.clearance] || CLEARANCE_DARK.None}`}>
                        <Shield className="h-3 w-3" />
                        {c.clearance === 'None' ? 'No clearance' : c.clearance}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                        <MapPin className="h-3 w-3 text-gray-500" />
                        {c.location}
                      </span>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SENIORITY_DARK[c.seniority] || ''}`}>
                        {c.seniority}
                      </span>
                    </div>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-gray-600 transition-colors duration-200 group-hover:text-red-400" />
                </div>
              ))}
            </div>
          </div>
        </Link>
      </Reveal>
    </div>
  </DarkSection>
);

/* ------------------------------- Testimonials ------------------------------- */
const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setDirection(1);
      setIndex((v) => (v + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const go = (dir) => {
    setDirection(dir);
    setIndex((v) => (v + dir + TESTIMONIALS.length) % TESTIMONIALS.length);
  };

  return (
    <section className="bg-gray-50 py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <SectionHeading eyebrow="Testimonials" title="What candidates and clients say" />

        <Reveal delay={0.15}>
          <div
            className="relative mt-12 overflow-hidden rounded-3xl border border-gray-200 bg-white p-8 shadow-xl shadow-gray-900/5 md:p-12"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <Quote className="h-10 w-10 text-red-100" fill="currentColor" />
            <div className="relative mt-4 min-h-[12rem] md:min-h-[10rem]">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.blockquote
                  key={index}
                  custom={direction}
                  initial={{ opacity: 0, x: 40 * direction }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 * direction }}
                  transition={{ duration: 0.45, ease: EASE }}
                >
                  <p className="text-lg italic leading-relaxed text-gray-700 md:text-xl">
                    {TESTIMONIALS[index].text}
                  </p>
                  <footer className="mt-6 font-display font-bold text-gray-900">
                    - {TESTIMONIALS[index].author}
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <div className="flex gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
                    aria-label={`Show testimonial ${i + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      i === index ? 'w-8 bg-red-500' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => go(-1)}
                  aria-label="Previous testimonial"
                  className="rounded-full border border-gray-200 p-2.5 text-gray-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Next testimonial"
                  className="rounded-full border border-gray-200 p-2.5 text-gray-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25} className="mt-6 text-center">
          <Link to="/testimonials" className="font-medium text-red-500 transition-colors hover:text-red-600 hover:underline">
            Share your testimonial here
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* --------------------------------- CTA band --------------------------------- */
const CtaBand = () => (
  <section className="bg-white py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gray-900 px-6 py-16 text-center text-white md:px-16 md:py-24">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-red-500/25 blur-3xl" />
            <div className="absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-red-500/15 blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="mx-auto max-w-3xl font-display text-3xl font-extrabold tracking-tight md:text-6xl">
              <KineticText text="Let's build capability together." />
            </h2>
            <Reveal delay={0.2}>
              <p className="mx-auto mt-6 max-w-xl text-lg text-gray-400">
                Whether you're sourcing cleared ICT talent or looking for your next engagement, get in touch today.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-8 py-4 text-lg font-semibold transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/30"
                >
                  Get in touch
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/jobs"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-lg font-semibold transition-all duration-300 hover:border-white hover:bg-white hover:text-gray-900"
                >
                  Browse jobs
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------------------------- Page ------------------------------------ */
const Home = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();

  // Redirect legacy one-page anchors (e.g. /#contact) to their new pages
  useEffect(() => {
    const section = hash.replace('#', '');
    if (section && HASH_REDIRECTS[section]) {
      navigate(HASH_REDIRECTS[section], { replace: true });
    }
  }, [hash, navigate]);

  return (
    <PublicLayout>
      <Hero />
      <StatsStrip />
      <AboutPreview />
      <ServicesBento />
      <SpecialisationsMarquee />
      <CandidatesTeaser />
      <Testimonials />
      <CtaBand />
    </PublicLayout>
  );
};

export default Home;
